/**
 * Session Management Utilities
 * Provides helper functions for timeout handling, retries, and session validation
 */

import { DEFAULT_SESSION_CONFIG } from "../shared/types/session.types";

/**
 * Timeout error thrown when an operation exceeds the time limit
 */
export class TimeoutError extends Error {
  constructor(message: string = "Operation timed out") {
    super(message);
    this.name = "TimeoutError";
  }
}

/**
 * Wraps a promise with a timeout
 * @param promise - The promise to wrap
 * @param ms - Timeout in milliseconds
 * @param errorMessage - Custom error message
 * @returns Promise that rejects with TimeoutError if timeout is exceeded
 */
export function withTimeout<T>(
  promise: Promise<T>,
  ms: number = DEFAULT_SESSION_CONFIG.apiTimeout,
  errorMessage?: string
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(
        () =>
          reject(
            new TimeoutError(
              errorMessage || `Operation timed out after ${ms}ms`
            )
          ),
        ms
      )
    ),
  ]);
}

/**
 * Retries a function with exponential backoff
 * @param fn - Function to retry
 * @param maxRetries - Maximum number of retry attempts
 * @param initialDelay - Initial delay in milliseconds
 * @param shouldRetry - Optional function to determine if error is retryable
 * @returns Promise resolving to the function result
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = DEFAULT_SESSION_CONFIG.maxRetries,
  initialDelay: number = DEFAULT_SESSION_CONFIG.retryDelay,
  shouldRetry?: (error: Error, attempt: number) => boolean
): Promise<T> {
  let lastError: Error;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Check if we should retry this error
      if (shouldRetry && !shouldRetry(lastError, attempt)) {
        throw lastError;
      }

      // Don't retry on last attempt
      if (attempt === maxRetries) {
        throw lastError;
      }

      // Calculate delay with exponential backoff
      const delay = initialDelay * Math.pow(2, attempt);

      // Add jitter to prevent thundering herd
      const jitter = Math.random() * delay * 0.1;

      await new Promise((resolve) => setTimeout(resolve, delay + jitter));
    }
  }

  throw lastError!;
}

/**
 * Checks if session data is stale
 * @param lastSync - Timestamp of last successful sync
 * @param maxAge - Maximum age in milliseconds
 * @returns true if session data is stale
 */
export function isSessionStale(
  lastSync: number | null,
  maxAge: number = DEFAULT_SESSION_CONFIG.maxSessionAge
): boolean {
  if (!lastSync) return true;
  return Date.now() - lastSync > maxAge;
}

/**
 * Clears all session-related data from storage
 */
export function clearSessionStorage(): void {
  try {
    // Clear specific session keys
    const keysToRemove = [
      "lawgentia-auth-storage",
      "redirectAfterAuth",
      "redirectAfterOnboarding",
    ];

    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  } catch (error) {
    console.error("Failed to clear session storage:", error);
  }
}

/**
 * Migrates session data to new schema version
 * @param currentVersion - Current schema version
 * @returns Migrated data or null if migration failed
 */
export function migrateSessionData(currentVersion: number): any {
  try {
    const storageKey = "lawgentia-auth-storage";
    const rawData = localStorage.getItem(storageKey);

    if (!rawData) return null;

    const data = JSON.parse(rawData);

    // Version 1: Original schema with multiple loading flags
    // Version 2: New schema with sessionStatus
    if (currentVersion < 2) {
      // Clear old data, force re-initialization
      clearSessionStorage();
      return null;
    }

    return data;
  } catch (error) {
    clearSessionStorage();
    return null;
  }
}

/**
 * Determines if an error is retryable
 * @param error - Error to check
 * @returns true if the error should trigger a retry
 */
export function isRetryableError(error: Error): boolean {
  // Network errors are retryable
  if (error.message.includes("network") || error.message.includes("fetch")) {
    return true;
  }

  // Timeout errors are retryable
  if (error instanceof TimeoutError) {
    return true;
  }

  // 5xx server errors are retryable
  if (error.message.includes("500") || error.message.includes("503")) {
    return true;
  }

  // Auth errors are not retryable (need user action)
  if (
    error.message.includes("unauthorized") ||
    error.message.includes("forbidden") ||
    error.message.includes("invalid token")
  ) {
    return false;
  }

  return false;
}

/**
 * Creates a mutex lock for preventing concurrent operations
 */
export class Mutex {
  private locked = false;
  private queue: Array<() => void> = [];

  async acquire(): Promise<void> {
    if (!this.locked) {
      this.locked = true;
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      this.queue.push(resolve);
    });
  }

  release(): void {
    const next = this.queue.shift();
    if (next) {
      next();
    } else {
      this.locked = false;
    }
  }

  async runExclusive<T>(fn: () => Promise<T>): Promise<T> {
    await this.acquire();
    try {
      return await fn();
    } finally {
      this.release();
    }
  }
}

/**
 * Global mutex for session initialization
 */
export const sessionMutex = new Mutex();
